/*
 * Knative Overview Card Component
 * Glassmorphism + Monospace Style
 */

import React from 'react';
import { Box, Typography, Card, CardHeader, CardContent, Tooltip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import LinkIcon from '@material-ui/icons/Link';
import { useKubernetesEnhancedStyles, colors } from '../styles/theme';
import { KnativeResourceOverview } from '../types';

interface KnativeOverviewCardProps {
  overview: KnativeResourceOverview;
  loading?: boolean;
}

interface OverviewItemProps {
  label: string;
  value: number | string;
  subValue?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

const OverviewItem: React.FC<OverviewItemProps> = ({ label, value, subValue, status }) => {
  const classes = useKubernetesEnhancedStyles();
  
  const getGradient = () => {
    switch (status) {
      case 'success': return colors.gradientSuccess;
      case 'warning': return colors.gradientWarning;
      case 'error': return colors.gradientError;
      case 'info': return colors.gradientCyan;
      default: return colors.gradientPrimary;
    }
  };
  
  return (
    <Box className={classes.overviewItem}>
      <Typography className={classes.overviewLabel}>{label}</Typography>
      <Typography 
        className={classes.overviewValue} 
        style={{ 
          background: getGradient(),
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {value}
      </Typography>
      {subValue && (
        <Typography className={classes.overviewStatus}>{subValue}</Typography>
      )}
    </Box>
  );
};

export const KnativeOverviewCard: React.FC<KnativeOverviewCardProps> = ({
  overview,
}) => {
  const classes = useKubernetesEnhancedStyles();
  
  const { service, revisions, pods, traffic } = overview;
  
  // Service 状态
  const serviceStatus = service.ready ? 'success' : 'error';
  
  // Pod 状态
  const podStatus = pods.running === pods.total && pods.total > 0 ? 'success' : 
                    pods.failed > 0 ? 'error' : 
                    pods.pending > 0 ? 'warning' : 'info';
  
  // Revision 状态
  const revisionStatus = revisions.ready === revisions.total && revisions.total > 0 ? 'success' : 
                         revisions.ready > 0 ? 'warning' : 'info';
  
  // 活跃流量的 Revision 数
  const activeRevisions = traffic.filter(t => (t.percent || 0) > 0).length;
  
  return (
    <Card className={classes.card}>
      <CardHeader
        title="// KNATIVE_OVERVIEW"
        className={classes.cardHeader}
        action={
          service.url ? (
            <Tooltip title="Open Service URL" arrow>
              <a 
                href={service.url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: colors.statusInfo, 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                <LinkIcon style={{ fontSize: '1rem', marginRight: 4 }} />
                {service.url.replace(/^https?:\/\//, '').slice(0, 30)}...
              </a>
            </Tooltip>
          ) : null
        }
      />
      <CardContent className={classes.cardContent}>
        {/* Service Status Banner */}
        <Box 
          mb={3} 
          p={2} 
          style={{ 
            background: service.ready 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(239, 68, 68, 0.1)',
            borderRadius: 8,
            border: `1px solid ${service.ready ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {service.ready ? (
            <CheckCircleIcon style={{ color: colors.statusSuccess, fontSize: '1.5rem' }} />
          ) : (
            <ErrorIcon style={{ color: colors.statusError, fontSize: '1.5rem' }} />
          )}
          <Box>
            <Typography style={{ 
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: service.ready ? colors.statusSuccess : colors.statusError,
            }}>
              {service.name}
            </Typography>
            <Typography style={{ 
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.75rem',
              color: colors.textMuted,
            }}>
              {service.ready ? 'Service is ready' : 'Service is not ready'}
            </Typography>
          </Box>
        </Box>
        
        {/* Stats Grid */}
        <Box className={classes.overviewGrid}>
          <OverviewItem
            label="REVISIONS"
            value={`${revisions.ready}/${revisions.total}`}
            subValue={`${activeRevisions} with traffic`}
            status={revisionStatus}
          />
          <OverviewItem
            label="PODS"
            value={`${pods.running}/${pods.total}`}
            subValue={pods.total > 0 ? `${pods.running} running` : 'scaled to zero'}
            status={podStatus}
          />
          <OverviewItem
            label="TRAFFIC"
            value={traffic.length}
            subValue={traffic.length > 0 ? 'targets' : 'no traffic'}
            status={traffic.length > 0 ? 'info' : undefined}
          />
          <OverviewItem
            label="STATUS"
            value={service.ready ? '✓' : '✗'}
            subValue={service.ready ? 'healthy' : 'unhealthy'}
            status={serviceStatus}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

